import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone, faMapMarkerAlt, faLanguage, faHeart, faLink } from '@fortawesome/free-solid-svg-icons';
import initialStyles from '../settings/initialStyles';
import { linkIcons, linkText } from '../settings/linkData';
import ThemeConfigPanel from '../settings/ThemeConfigPanel';
import Notification from '../components/notification/Notification'; // Ensure correct import path

import 'react-circular-progressbar/dist/styles.css';
import '../styles/TemplateOne.css';

const themePresets = {
    default: initialStyles,
    dark: {
        backgroundColor: '#333',
        secondaryBackgroundColor: '#555',
        headerBackgroundColor: '#222',
        headerTextColor: '#fff',
        subheaderTextColor: '#555',
        textColor: '#eee',
        headerFontSize: '1.6',
        subheaderFontSize: '1.5',
        bodyFontSize: '1'
    },
    light: {
        backgroundColor: '#f9f9f9',
        secondaryBackgroundColor: '#e0e0e0',
        headerBackgroundColor: '#ddd',
        headerTextColor: '#333',
        subheaderTextColor: '#555',
        textColor: '#111',
        headerFontSize: '1.6',
        subheaderFontSize: '1.5',
        bodyFontSize: '1'
    }
};

const ViewTemplateOne = ({ template, isDownloading }) => {

    const [styles, setStyles] = useState(initialStyles);
    const [notification, setNotification] = useState(null);

    const handleStyleChange = (e) => {
        const { name, value } = e.target;
        setStyles((prevStyles) => ({
            ...prevStyles,
            [name]: value,
        }));
    };

    const applyPreset = (preset) => {
        setStyles(themePresets[preset]);
    };

    const saveCustomTheme = () => {
        localStorage.setItem('customTheme', JSON.stringify(styles));
        setNotification({ message: 'Custom theme saved!', isError: false });
    };

    const loadCustomTheme = () => {
        const customTheme = localStorage.getItem('customTheme');
        if (customTheme) {
            setStyles(JSON.parse(customTheme));
            setNotification({ message: 'Custom theme loaded!', isError: false });
        } else {
            setNotification({ message: 'No custom theme found!', isError: true });
        }
    };

    const templateData = template || {};

    const skillsWithLevels = (templateData?.skills || []).map(skill => ({
        name: skill,
        level: Math.floor(Math.random() * 11) + 90

    }));

    const softwareSkillsWithLevels = (templateData?.softwareSkills || []).map(skill => ({
        name: skill,
        level: Math.floor(Math.random() * 11) + 90

    }));


    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://venusresume.vercel.app/';
    const imageUrl = templateData?.image?.startsWith('/')
        ? `${baseUrl}${templateData.image}`
        : templateData.image;

    return (
        <div className='holder'>

            {notification && (
                <Notification
                    message={notification.message}
                    isError={notification.isError}
                    onClose={() => setNotification(null)}
                />
            )}

            <div className="template-one" style={{ backgroundColor: styles.backgroundColor }}>
                <div className="content-wrapper">
                    <div className="sidebar" style={{ backgroundColor: styles.secondaryBackgroundColor }}>
                        <div style={{ backgroundColor: styles.headerBackgroundColor, padding: "20px", width: "100%", marginRight: "13px", marginLeft: "13px" }}>
                            {imageUrl && (
                                <div className="image-section">
                                    <img src={imageUrl} alt={template?.personName} className="template-image" />
                                </div>
                            )}
                            {template?.personName && <h1 className="template-name" style={{ fontSize: `${styles.headerFontSize}em`, color: styles.headerTextColor }}>{template?.personName}</h1>}
                        </div>

                        {templateData?.contact && (
                            <div className="template-contact">
                                <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Contact:</strong>
                                {templateData.contact.email && <p><FontAwesomeIcon icon={faEnvelope} style={{ fontSize: `${styles.bodyFontSize}em` }} /> {templateData.contact.email}</p>}
                                {templateData.contact.phone && <p><FontAwesomeIcon icon={faPhone} style={{ fontSize: `${styles.bodyFontSize}em` }} /> {templateData.contact.phone}</p>}
                                {templateData.contact.address && <p><FontAwesomeIcon icon={faMapMarkerAlt} style={{ fontSize: `${styles.bodyFontSize}em` }} /> {templateData.contact.address}</p>}
                            </div>
                        )}

                        {templateData?.languages && (
                            <div className="template-languages">
                                <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}><FontAwesomeIcon icon={faLanguage} /> Languages:</strong> {templateData.languages.join(', ')}
                            </div>
                        )}

                        {templateData?.hobbies && (
                            <div className="template-hobbies">
                                <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}><FontAwesomeIcon icon={faHeart} /> Hobbies:</strong> {templateData.hobbies.join(', ')}
                            </div>
                        )}

                        {templateData?.links && (
                            <div className="template-links">
                                <strong style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Links:</strong>
                                {Object.entries(templateData.links).map(([key, value]) => (
                                    <p key={key}>
                                        <FontAwesomeIcon icon={linkIcons[key] || faLink} /> <a href={value} target="_blank" rel="noopener noreferrer">{linkText[key]}</a>
                                    </p>
                                ))}
                            </div>
                        )}

                    </div>
                    <div className="main-content">
                        <div className="template-about template-section">
                            <h2 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>About Me</h2>
                            {templateData.aboutMe && <p className="template-about" style={{ fontSize: `${styles.bodyFontSize}em`, color: styles.textColor }}>{templateData.aboutMe}</p>}
                        </div>
                        {templateData?.skills && (
                            <div className="template-skills template-section">
                                <h2 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Skills</h2>
                                <div className="skills-wrapper">
                                    {skillsWithLevels.map((skill, index) => (
                                        <div key={index} className="skill-item">
                                            <p style={{ fontSize: `${styles.bodyFontSize}em` }}>{skill.name}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {templateData?.softwareSkills && templateData.softwareSkills.length > 0 && (
                            <div className="template-skills template-section">
                                <h2 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Software Skills</h2>
                                <div className="skills-wrapper">
                                    {softwareSkillsWithLevels.map((skill, index) => (
                                        <div key={index} className="skill-item">
                                            <p style={{ fontSize: `${styles.bodyFontSize}em` }}>{skill.name}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}


                        {templateData?.workExperience && templateData.workExperience.length > 0 && (
                            <div className="template-work-experience template-section">
                                <h2 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Work Experience</h2>
                                {templateData.workExperience.map((experience, index) => (
                                    <div key={index} className="work-experience-item" >
                                        {experience.role && <p className="role" style={{ fontSize: `${styles.bodyFontSize}em` }}>{experience.role} at {experience.company}</p>}
                                        {experience.location && (
                                            <p className="location" style={{ fontSize: `${styles.bodyFontSize}em` }}>
                                                {experience.location} (
                                                {experience.startDate && new Date(experience.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} -
                                                {experience.endDate !== "Present" ? new Date(experience.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Present'}
                                                )
                                            </p>
                                        )}
                                        {experience.description && <p className="description" style={{ fontSize: `${styles.bodyFontSize}em` }}>{experience.description}</p>}
                                    </div>
                                ))}
                            </div>
                        )}
                        {templateData?.education && templateData.education.length > 0 && (
                            <div className="template-education template-section">
                                <h2 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Education</h2>
                                {templateData.education.map((education, index) => (
                                    <div key={index} className="education-item" >
                                        {education.degree && <p className="degree" style={{ fontSize: `${styles.bodyFontSize}em` }}>{education.degree} at {education.institution}</p>}

                                        <p style={{ fontSize: `${styles.bodyFontSize}em`, fontWeight: "200" }}>
                                            (
                                            {education.startDate && new Date(education.startDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} -
                                            {education.endDate !== "Present" ? new Date(education.endDate).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) : 'Present'}
                                            )
                                        </p>

                                        {education.description && <p className="description" style={{ fontSize: `${styles.bodyFontSize}em` }}>{education.description}</p>}

                                    </div>
                                ))}
                            </div>
                        )}

                        {templateData?.certificates && templateData.certificates.length > 0 && (
                            <div className="template-education template-section">
                                <h2 style={{ fontSize: `${styles.subheaderFontSize}em`, color: styles.subheaderTextColor }}>Certificates</h2>
                                {templateData.certificates.map((certification, index) => (
                                    <div key={index} className="education-item degree" >
                                        {certification.title && <p className="name" style={{ fontSize: `${styles.bodyFontSize}em` }}>Title: {certification.title}</p>}
                                        {certification.institution && <p style={{ fontSize: `${styles.bodyFontSize}em`, fontWeight: "300" }}>Institution: {certification.institution}</p>}
                                        {certification.DateReceived && (
                                            <p className='institution' style={{ fontSize: `${styles.bodyFontSize}em`, fontWeight: "200" }}>
                                                Date Received: {new Date(certification.DateReceived).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                                            </p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}

                    </div>
                </div>
            </div>
            {!isDownloading && (
                <ThemeConfigPanel
                    styles={styles}
                    handleStyleChange={handleStyleChange}
                    applyPreset={applyPreset}
                    saveCustomTheme={saveCustomTheme}
                    loadCustomTheme={loadCustomTheme}
                />
            )}
        </div>
    );
};

export default ViewTemplateOne;
